import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { Subscription } from 'rxjs';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { UppKonvaModel } from '@unpispas/upp-konva';
import { UppKonvaShape } from '@unpispas/upp-konva';

import type { UppKonvaOrientation } from '@unpispas/upp-konva';

import { DrawItem } from '@unpispas/upp-data';
import { FloorArea } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';

import { UppFloorUtils } from '../common/upp-area';
import { UppCommon } from '../konva/upp-common';
import type { UppViewTransform } from '../konva/upp-render';

export type UppDrawSelectExtra = Readonly<Record<string, unknown>>;
export type UppSelectedEvent = {
    readonly draw: DrawItem | null;
    readonly extra: UppDrawSelectExtra;
};

/** Hide delay for the transient zoom badge after the last VIEW navigation gesture. */
const FLOOR_VIEW_ZOOM_INDICATOR_HIDE_MS = 1000;

/************************************/
/* KONVA VIEW ADAPTER               */
/************************************/

/**
 * Read-only bridge: projects {@link FloorArea} onto {@link UppKonvaModel} in {@link UppKonvaModel.mode} VIEW
 * and forwards canvas selection to the host.
 */
class ViewKonvaAdapter {
    /** Default Konva host margin (CSS px) for desktop VIEW framing. */
    private static readonly _DESKTOP_MARGIN_PX = 12;
    /** Tighter host margin on mobile VIEW to grow the fitted room in the portrait frame. */
    private static readonly _MOBILE_MARGIN_PX = 4;

    private _floor_subscription: Subscription | null = null;
    private _area_subscription: Subscription | null = null;
    private _theme_subscription: Subscription | null = null;
    private _lang_subscription: Subscription | null = null;

    private readonly _draw_subscriptions = new Map<DrawItem, Subscription>();

    private get area(): FloorArea | null{
        return this._parent.area;
    }

    private get view(): viewService {
        return this._parent.view;
    }

    private get change(): ViewRenderer {
        return this._parent.change;
    }

    private get konva(): UppKonvaModel | null {
        return this._parent.konva;
    }

    constructor(private readonly _parent: UppFloorViewComponent) {
        if (this.konva) {
            this.konva.mode = 'VIEW';
            this._ApplyViewMargins();

            if (this.view) {
                this.konva.theme = this.view.Theme === 'light' ? 'UPPKONVA_LIGHTTHEME' : 'UPPKONVA_DARKTHEME';
                this._theme_subscription = this.view.OnTheme.subscribe(() => {
                    if (this.konva) {
                        this.konva.theme = this.view?.Theme === 'light' ? 'UPPKONVA_LIGHTTHEME' : 'UPPKONVA_DARKTHEME';
                    }
                });
            }

            if (this.area) {
                this.Redraw();
                this._floor_subscription = this.area.OnRefresh.subscribe(() => {
                    this.Redraw();
                });
            } 
            else {
                this._ApplyViewArea(UppCommon.EmptyViewArea(this.konva));
            }

            this._lang_subscription = this._parent._lang.OnSetLanguage.subscribe(() => {
                queueMicrotask(() => {
                    if (!this.area) {
                        return;
                    }

                    for (const draw of this.area.draws) {
                        if (draw.IsValid && draw.type === 'TABLE') {
                            this.OnQrcodeUpdate(draw);
                        }
                    }
                });
            });

            if (this.konva.area) {
                const areaSub = new Subscription();
                areaSub.add(
                    this.konva.area.onClearSelection$.subscribe(() => {
                        this._parent.OnDrawSelected(null, {});
                    }),
                );
                this._area_subscription = areaSub;
            }
        }
    }

    OnDestroy(): void {
        if (this._floor_subscription) {
            this._floor_subscription.unsubscribe();
            this._floor_subscription = null;
        }

        if (this._area_subscription) {
            this._area_subscription.unsubscribe();
            this._area_subscription = null;
        }

        if (this._theme_subscription) {
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        if (this._lang_subscription) {
            this._lang_subscription.unsubscribe();
            this._lang_subscription = null;
        }

        for (const sub of this._draw_subscriptions.values()) {
            sub.unsubscribe();
        }
        this._draw_subscriptions.clear();
    }

    Redraw(): void {
        if (!this.konva?.area) {
            return;
        }

        if (!this.area) {
            this._ApplyViewArea(UppCommon.EmptyViewArea(this.konva));
            for (const sub of this._draw_subscriptions.values()) {
                sub.unsubscribe();
            }
            this._draw_subscriptions.clear();
            
            return;
        }

        UppFloorUtils.RefreshTableQrcodeDrawRefs(this.area);

        this._ApplyViewArea(
            UppCommon.AreaToKonva(this.area, this.konva, 'SHAPES', (draw) => this._tableLabels(draw)),
        );

        for (const sub of this._draw_subscriptions.values()) {
            sub.unsubscribe();
        }
        this._draw_subscriptions.clear();

        for (const draw of this.area.draws) {
            if (draw.IsValid) {
                this._setKonvaSource(draw);
            }
        }
    }

    /**
     * Sets host framing margins for the current view mode, then pushes the area DTO to Konva.
     * Mobile VIEW uses a tighter margin so the fitted room uses more of the portrait frame.
     */
    private _ApplyViewArea(source: ReturnType<typeof UppCommon.AreaToKonva>): void {
        if (!this.konva) {
            return;
        }
        this._ApplyViewMargins();
        this.konva.applyAreaFromSource(source);
    }

    /** Writes {@link UppKonvaModel.marginPx} from {@link viewService.Mobile} (not viewport width). */
    private _ApplyViewMargins(): void {
        if (!this.konva) {
            return;
        }
        const m = this.view?.Mobile
            ? ViewKonvaAdapter._MOBILE_MARGIN_PX
            : ViewKonvaAdapter._DESKTOP_MARGIN_PX;
        this.konva.marginPx = [m, m, m, m];
        this.konva.applyHostPresentationPreferences();
    }

    /** Maps a live canvas shape to its {@link DrawItem} row on the floor area. */
    private _drawForShape(shape: UppKonvaShape): DrawItem | null {
        if (!this.area) {
            return null;
        }

        return UppFloorUtils.ResolveDrawInArea(this.area, shape.id);
    }

    OnCanvasSelect(shapes: readonly UppKonvaShape[]): void {
        if (shapes.length !== 1) {
            return;
        }

        const _shape = shapes[0];
        if (!_shape) {
            return;
        }

        const draw = this._drawForShape(_shape);
        if (!draw) {
            return;
        }

        const extra: UppDrawSelectExtra = {};
        this._parent.OnDrawSelected(draw, extra);
    }

    private _setKonvaSource(draw: DrawItem): void {
        if (!this.area) {
            return;
        }

        const existing = this._draw_subscriptions.get(draw);
        if (existing) {
            existing.unsubscribe();
            this._draw_subscriptions.delete(draw);
        }

        draw.konva = null;
        if (!this.konva?.area) {
            return;
        }

        const shape = this.konva.area.findShapeById(draw._uuid);
        if (shape) {
            draw.konva = shape;

            const _subscription = new Subscription();

            const qrcode = draw.qrcode;
            if (qrcode) {
                const _qrcode = this._parent.data.alives.get(qrcode) as QrCodeView | null;
                if (_qrcode) {
                    _subscription.add(
                        _qrcode.OnViewRefresh.subscribe(() => {
                            queueMicrotask(() => {
                                this.OnQrcodeUpdate(draw);
                            });
                        }),
                    );
                }
            }

            this._draw_subscriptions.set(draw, _subscription);

            queueMicrotask(() => {
                this.OnQrcodeUpdate(draw);
            });
        }
    }

    /**
     * Etiqueta principal: comensales ocupados si la mesa está en `busy`, plazas libres en el resto de estados.
     * Secundaria: identificador de mesa (alias o número).
     */
    private _tableLabels(draw: DrawItem): { label?: string; sublabel?: string } {
        if (!draw.qrcode) {
            return {};
        }

        const qrcode: QrCodeView = this._parent.data.alives.get(draw.qrcode) as QrCodeView;
        const isbusy: boolean = ['busy', 'goto'].includes(qrcode.Status);
        const _diners = (isbusy) ? qrcode.diners || 0 : qrcode.chairs || 0;
        const label: string = (!qrcode.alias || qrcode.alias === '') ? this._parent.tr('@place_table_number', [ qrcode.number ]) : qrcode.alias;
        const small: string = this._parent.tr('@place_table_diners', [ _diners ]);

        return {
            label: isbusy ? small : label,
            sublabel: isbusy ? label : small
        };
    }

    private OnQrcodeUpdate(draw: DrawItem): void {
        const shape = draw.konva as UppKonvaShape | null;
        if (!shape || draw.type !== 'TABLE') {
            return;
        }

        const badge = this._tableLabels(draw);
        shape.label = badge.label ?? '';
        shape.sublabel = badge.sublabel ?? '';
        shape.syncLabelsToStage();

        if (draw.qrcode) {
            const qrcode: QrCodeView = this._parent.data.alives.get(draw.qrcode) as QrCodeView;
            
            const diners = ['busy', 'goto'].includes(qrcode.Status) ? qrcode.diners || 0 : 0;
            shape.data = { ...shape.data, diners: diners };
            shape.syncPresentationToStage();

            switch (qrcode.Status) {
                case 'goto':
                    shape.setHighlight('WARNING', true);
                    break;
    
                case 'busy':
                    shape.setHighlight('SUCCESS', false);
                    break;
    
                default:
                    shape.clearHighlight();
                    break;
            }
        }
        else {
            shape.data = { ...shape.data, diners: 0 };
            shape.syncPresentationToStage();

            shape.clearHighlight();
        }
    }
}

/************************************/
/* FLOOR PLAN VIEW (CANVAS)         */
/************************************/

@Component({
    selector: 'upp-floor-view',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-view.html',
    styleUrls: ['./upp-view.scss'],
    providers: [{
        provide: ViewRenderer,
        useFactory: (cd: ChangeDetectorRef) => new ViewRenderer(cd),
        deps: [ChangeDetectorRef],
    }],
})
export class UppFloorViewComponent extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input() area: FloorArea | null = null;
    @Input() orientation: UppKonvaOrientation = 'AUTO';

    @Output() readonly OnSelected = new EventEmitter<UppSelectedEvent>();
    @Output() readonly OnTransform = new EventEmitter<UppViewTransform>();

    konva: UppKonvaModel | null = null;

    /** Transient VIEW zoom badge (top-right); hidden {@link FLOOR_VIEW_ZOOM_INDICATOR_HIDE_MS} after last gesture. */
    showZoom = false;
    zoomLabel = '100%';

    private _konvaAdapter: ViewKonvaAdapter | null = null;
    private _zoomTimer: ReturnType<typeof setTimeout> | null = null;

    get KonvaAdapt(): ViewKonvaAdapter | null {
        return this._konvaAdapter;
    }

    constructor(
        lang: languageService,
        public readonly change: ViewRenderer,
        public readonly view: viewService,
        public readonly data: dataService,
    ) {
        super(lang, change.cdref);
    }

    ngOnInit(): void {
        this._syncKonvaAdapter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['area']) {
            this._syncKonvaAdapter();
        }
    }

    /** Child `upp-floor-render` creates the `UppKonvaModel` and emits it here (see `upp-view.mdc`). */
    onKonvaFromRender(model: UppKonvaModel | null): void {
        this.konva = model;
        this._syncKonvaAdapter();
    }

    onKonvaSelect(shapes: readonly UppKonvaShape[]): void {
        this._konvaAdapter?.OnCanvasSelect(shapes);
    }

    /**
     * Shows the zoom percentage badge while the user pans/zooms the plan; hides it 1 s after the last change.
     * Re-emits {@link OnTransform} for parent hosts (floor swipe gate, etc.).
     */
    onViewTransformFromRender(payload: UppViewTransform): void {
        const z = Number.isFinite(payload?.zoomFactor) ? payload.zoomFactor : 1;
        this.zoomLabel = `${Math.round(z * 100)}%`;
        this.showZoom = true;
        if (this._zoomTimer != null) {
            clearTimeout(this._zoomTimer);
        }
        this._zoomTimer = setTimeout(() => {
            this._zoomTimer = null;
            this.showZoom = false;
            this.change.markForCheck();
        }, FLOOR_VIEW_ZOOM_INDICATOR_HIDE_MS);
        this.OnTransform.emit(payload);
        this.change.markForCheck();
    }

    private _syncKonvaAdapter(): void {
        if (this._konvaAdapter) {
            this._konvaAdapter.OnDestroy();
            this._konvaAdapter = null;
        }

        if (this.konva) {
            this._konvaAdapter = new ViewKonvaAdapter(this);
        }

        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._zoomTimer != null) {
            clearTimeout(this._zoomTimer);
            this._zoomTimer = null;
        }

        if (this._konvaAdapter) {
            this._konvaAdapter.OnDestroy();
            this._konvaAdapter = null;
        }
    }

    OnDrawSelected(draw: DrawItem | null, extra: UppDrawSelectExtra): void {
        this.OnSelected.emit({ draw, extra });
        this.change.markForCheck();
    }
}
